import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { config } from '@/lib/config';
import { TURNSTILE_RETRY_DELAY_MS, turnstileFailureAction } from '@/lib/turnstile';

/**
 * Invisible Cloudflare Turnstile widget. Calls onToken when a token is ready. "Invisible" is the
 * widget mode set in the Cloudflare dashboard, not a `size` (the API rejects `size: 'invisible'`);
 * `interaction-only` keeps a Managed widget hidden unless a visitor must be challenged.
 *
 * A failed challenge (error-callback) is handled here rather than thrown by Turnstile: a few quiet
 * retries, then a message with a retry button, so the visitor is never stuck on a disabled submit.
 */
export function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const idRef = useRef<string | null>(null);
  const failuresRef = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [failed, setFailed] = useState(false);

  const reset = useCallback(() => {
    if (idRef.current) window.turnstile?.reset(idRef.current);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile) {
        setTimeout(tryRender, 100);
        return;
      }
      idRef.current = window.turnstile.render(el, {
        sitekey: config.turnstileSiteKey,
        appearance: 'interaction-only',
        // Retries are ours (below); Turnstile's own would race them.
        retry: 'never',
        callback: (token: string) => {
          failuresRef.current = 0;
          setFailed(false);
          onToken(token);
        },
        'expired-callback': () => {
          onToken('');
          reset();
        },
        'timeout-callback': reset,
        'error-callback': () => {
          onToken('');
          failuresRef.current += 1;
          if (turnstileFailureAction(failuresRef.current) === 'retry') {
            retryTimer.current = setTimeout(reset, TURNSTILE_RETRY_DELAY_MS);
          } else {
            setFailed(true);
          }
          // Handled: without this Turnstile throws the error (Sentry GRAVEL-GAMES-WEB-2).
          return true;
        },
      });
    };
    tryRender();
    return () => {
      cancelled = true;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (idRef.current) window.turnstile?.remove(idRef.current);
    };
  }, [onToken, reset]);

  const retry = () => {
    failuresRef.current = 0;
    setFailed(false);
    reset();
  };

  return (
    <div>
      <div ref={ref} />
      {failed && (
        <div role="alert" className="flex flex-col items-start gap-2 text-sm text-danger">
          <p>{t('turnstile.failed')}</p>
          <div className="shrink-0">
            <Button variant="secondary" size="inline" onClick={retry}>
              {t('turnstile.retry')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
